import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { db, ensureSettings, getActiveDb } from '../../lib/db'
import { defaultStoreResolver } from '../../lib/storeBinding'
import { runInitialSync, type InitialSyncProgress } from '../../lib/sync'
import { syncOneDriveFonts } from '../../lib/customFonts'
import { useAuth } from '../../lib/authContext'
import { decideAuthView } from '../../lib/authGate'
import { shouldAutoLogin } from '../../lib/authAutoLogin'
import {
  hasAttemptedAutoLogin,
  hasSignedOutDeliberately,
  isReturningVisitor,
  markAutoLoginAttempted,
  markReturningVisitor,
} from '../../lib/authFlags'
import GrainField from '../GrainField'
import Wordmark from '../Wordmark'
import LandingPage from '../../pages/Landing'
import { LoadingGate, FailedGate, StoreFailedGate } from './LoadingGate'
import styles from './AuthGate.module.css'

/**
 * QUIL — OneDrive-first mandatory sign-in gate (Phase 2).
 *
 * Wraps the app: nothing renders until a Microsoft account is present. Flow:
 *  - initialising / handling a redirect → neutral splash (no sign-in flash)
 *  - no account → the landing page (`pages/Landing`), which is the app's ONE front door
 *  - account present → a **cold-start barrier**: on a device that hasn't completed
 *    its first gated sync (no local works, or initialSyncComplete unset) show
 *    the cold-start barrier while runInitialSync pulls the cloud and pushes any
 *    local-only data up; a device that already has synced data paints instantly.
 *
 * Returning users pass the gate offline (MSAL caches the account in localStorage),
 * so offline writing is preserved; only a never-signed-in device is blocked.
 */

// Exported for BuildGate, which holds the same neutral splash while it checks the deployed build.
export function Splash({ label }: { label?: string }) {
  return (
    // id/tabIndex keep the shell's skip-link target (#main) alive in this state too
    // (App.tsx's own #main wrapper isn't mounted until AuthGate reaches 'ready').
    <div id="main" tabIndex={-1} className={styles.splash} role="status" aria-live="polite" aria-label={label ?? 'Loading'}>
      <GrainField />
      <div className={styles.splashStack}>
        <Wordmark className={styles.splashWordmark} />
        {label && <div className={styles.splashLabel}>{label}</div>}
      </div>
    </div>
  )
}

/* The short SignInPage that used to live here is GONE (P4, 2026-09-22).
 *
 * ONE FRONT DOOR. Every signed-out visitor now gets `pages/Landing`, returning or not. Quil never
 * had a marketing page at all, so this is a build rather than a deletion — but the shape is the
 * same change the other four apps make, decided by Kamrin during the W9 verification on
 * 2026-09-19 (app-platform/BEFORE-PORTFOLIO.md) and reversing the landing-page retrofit of
 * 2026-09-11. It is recorded in build/decisions.md so it does not read as drift.
 *
 * Two things moved with it and neither is optional:
 *
 *   · **"Keep writing offline" is on the landing page now.** It is the D8 captive-portal escape:
 *     a dead network that still reports `onLine === true` would otherwise strand a trusted device
 *     away from a library it already holds locally. Deleting the screen without rehoming the
 *     button would have reintroduced that bug.
 *   · **`returningVisitor` keeps its other job.** It chose the screen AND gates the auto-login
 *     bounce; only the first goes away. Removing it outright would throw a brand-new visitor at
 *     Microsoft before they had seen anything, which is the defect W9 closed here.
 *
 * The page is imported EAGERLY, on purpose. It is the front door on every signed-out visit rather
 * than a lazy chunk a returning visitor never pays for, and a `lazy()` here would put a second
 * round trip in front of a stranger's first sight of the app — the four-to-five-second cold load
 * BEFORE-PORTFOLIO.md measured on two of the siblings. It carries no library, no imagery and no
 * extra typeface for exactly that reason.
 */

// The signed-in device's cold-start decision. 'deciding' is a brief blank while we
// check local data + settings; 'syncing' shows the barrier; 'failed' shows the honest
// error gate (only when sync failed AND there's no local library); 'ready' renders the app.
type AuthedPhase = 'deciding' | 'syncing' | 'failed' | 'ready'

// The cold-start barrier can never strand the user: if the first sync hasn't settled
// within this window (a hung network, a huge migration), we enter the app anyway —
// the sync queue persists and the background listeners keep draining it.
const COLD_START_TIMEOUT_MS = 30_000

// MSAL v5 encrypts the localStorage cache with a session-cookie key, so the account is
// dropped on browser close and can only be kept via "Keep me signed in" — a prompt that
// never appears in Kamrin's Edge+passkey flow. So instead of making him click "Sign in"
// on every reopen, we AUTO-start the redirect: his persistent Microsoft/passkey session
// makes it a near-instant silent bounce back into the app. See build/decisions.md (Auth
// 2026-07-06). The once-per-tab flag, and the four other guards that decide whether the
// bounce is allowed at all, now live in the canonical authFlags.ts / authAutoLogin.ts.

// Dev-only escape hatch. With VITE_DEV_BYPASS_AUTH=1 in a DEV build (set in an
// un-committed .env.local), skip the Microsoft sign-in gate entirely and render the
// app straight against the local Dexie store — so the browser preview, which can't
// reach login.microsoftonline.com, can actually exercise the UI. Double-guarded by
// import.meta.env.DEV so a PRODUCTION build can never bypass auth even if the flag
// leaks in. OneDrive sync simply no-ops without a token (uploads queue + retry);
// local drafting/planning works fully offline, which is all the UI tests need.
const DEV_BYPASS_AUTH =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&   // never bypass under Vitest (it loads .env.local + runs in DEV)
  import.meta.env.VITE_DEV_BYPASS_AUTH === '1'

export function AuthGate({ children }: { children: ReactNode }) {
  if (DEV_BYPASS_AUTH) return <LocalOnlyGate>{children}</LocalOnlyGate>
  return <AuthGateInner>{children}</AuthGateInner>
}

/**
 * The dev bypass still needs a store (W12 A5): nothing opens one by accident any more, so this binds
 * the one local database (no account, no sync) before the app renders against it.
 */
function LocalOnlyGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(() => getActiveDb() !== null)
  useEffect(() => {
    if (ready) return
    let live = true
    void defaultStoreResolver.bindLocalOnly().then(() => { if (live) setReady(true) })
    return () => { live = false }
  }, [ready])
  return ready ? <>{children}</> : <Splash />
}

function AuthGateInner({ children }: { children: ReactNode }) {
  const { account, isInitializing, login, storeState, retryStore, canOpenOffline, openOffline } = useAuth()
  const storeReady = storeState.status === 'ready'
  // A read-only store (newer data, or one that could not be upgraded safely) is shown, never synced.
  const storeReadOnly = storeState.status === 'ready' && storeState.store.readOnly !== null
  const [authedPhase, setAuthedPhase] = useState<AuthedPhase>('deciding')
  const [progress, setProgress] = useState<InitialSyncProgress | null>(null)
  const [autoFailed, setAutoFailed] = useState(false)
  // Bumped by the failure gate's "Try again" to re-run the cold-start sync.
  const [retryNonce, setRetryNonce] = useState(0)
  // Guard the one-time cold-start run against StrictMode's double-invoke / re-renders.
  const startedForRef = useRef<string | null>(null)
  const autoTriedRef = useRef(false)
  // Trusted device (audit 2.2 + D8): a device that has completed a real sign-in before
  // (settings.oneDriveUserId persists in Dexie) already holds its library locally.
  // MSAL v5 drops the account on a full browser close and the auto-login redirect needs
  // a live network, so such a device must still be able to reach its own fully-local
  // library when Microsoft is unreachable. Loaded REGARDLESS of navigator.onLine, because
  // a captive portal / dead link that still reports `onLine === true` (D8) is exactly the
  // case the old `!navigator.onLine` check missed. Everything then runs against Dexie;
  // sync stays paused (the token getter reports InteractiveSignInRequiredError, which
// isAuthInteractionError matches by type → B4 pause) until the next
  // online sign-in. A device that has never signed in stays gated (nothing local to show).
  //
  // W12 A5: "trusted" is no longer a read of the one legacy database's `oneDriveUserId` (which any
  // account's sign-in used to overwrite). It is: this device already holds a library it KNOWS is an
  // account's, by that store's own owner stamp or its scoped marker (`canOpenOffline`). It opens
  // that library and only that one, and never syncs it.
  const [trustedDevice, setTrustedDevice] = useState(false)
  const [enteredOffline, setEnteredOffline] = useState(false)
  const [offlineOpened, setOfflineOpened] = useState(false)
  const [offlineFailed, setOfflineFailed] = useState(false)
  useEffect(() => {
    if (isInitializing || account) return
    let cancelled = false
    void canOpenOffline().then(trusted => {
      if (cancelled) return
      // MIGRATION (W9, 2026-09-19). `quil.hasSignedInBefore` is a new flag, and the auto-login
      // bounce is now gated on it. It is only written when an account is successfully RESTORED,
      // and the bounce exists precisely for the case where MSAL cannot restore one (a browser
      // close invalidates the session-cookie key that encrypts its cache). So without this, every
      // device Kamrin already uses would meet the sign-in page once instead of being bounced
      // straight in — a regression introduced by the guard, not by anything he did.
      //
      // `oneDriveUserId` is the durable answer to the same question, already on the device: it is
      // written on a completed sign-in and is what `trustedDevice` means. A device that has synced
      // has plainly signed in before.
      if (trusted) markReturningVisitor()
      setTrustedDevice(trusted)
    })
    return () => { cancelled = true }
  }, [isInitializing, account, canOpenOffline])

  useEffect(() => {
    if (isInitializing || !account) { startedForRef.current = null; return }
    // W12 A5: nothing reads or syncs the library until this account has been matched to its store.
    // Depending on the boolean (not the state object) keeps this effect from being torn down, and
    // the in-flight cold start cancelled, by an unrelated re-render.
    if (!storeReady) return
    // Key on account + retry nonce so a manual retry re-runs but re-renders don't.
    const runKey = `${account.homeAccountId}:${retryNonce}`
    if (startedForRef.current === runKey) return
    startedForRef.current = runKey

    // Register any OneDrive-dropped custom fonts (once per account, warm or cold
    // paint). Fire-and-forget: independent of the library-sync barrier below, and
    // FontFaces must be re-registered each launch since they don't persist.
    if (!storeReadOnly) {
      void syncOneDriveFonts().catch(err =>
        console.warn('[Quil] custom font sync failed', err))
    }

    let cancelled = false
    // Safety net: never let the barrier strand the user (see COLD_START_TIMEOUT_MS).
    const timeout = setTimeout(() => {
      if (!cancelled) { console.warn('[Quil] cold-start sync timed out; entering app'); setAuthedPhase('ready') }
    }, COLD_START_TIMEOUT_MS)

    void (async () => {
      setAuthedPhase('deciding')
      setProgress(null)
      // Nothing is pulled into, or pushed from, a store that is open read-only: show what it holds.
      if (storeReadOnly) { if (!cancelled) setAuthedPhase('ready'); return }
      const [workCount, settings] = await Promise.all([db.works.count(), ensureSettings()])
      // Instant paint when this device has already done its first gated sync AND
      // has local data to show; otherwise run the cold-start barrier once.
      if (workCount > 0 && settings.initialSyncComplete) {
        if (!cancelled) setAuthedPhase('ready')
        return
      }
      if (!cancelled) setAuthedPhase('syncing')
      try {
        await runInitialSync(p => { if (!cancelled) setProgress(p) })
        if (!cancelled) setAuthedPhase('ready')
      } catch (err) {
        console.warn('[Quil] initial sync failed', err)
        // If we already hold a library locally, offline is normal — just enter the
        // app (background sync retries). Only when there's nothing to show do we
        // surface the honest failure gate rather than a false-empty library.
        const haveLibrary = (await db.works.count()) > 0
        if (!cancelled) setAuthedPhase(haveLibrary ? 'ready' : 'failed')
      }
    })()
    return () => { cancelled = true; clearTimeout(timeout) }
  }, [account, isInitializing, retryNonce, storeReady, storeReadOnly])

  const retry = useCallback(() => { setProgress(null); setRetryNonce(n => n + 1) }, [])

  // Auto sign-in: once init has settled with no account, kick off the redirect so a
  // returning device signs straight back in without a click. Skip (show the manual page)
  // if we've already tried this tab-session or we're offline — no loop, no stranding on
  // an unreachable Microsoft page.
  //
  // Computed during render so the gate below can tell "a redirect is about to happen" (hold a
  // splash) from "no redirect is coming" (show the sign-in page now). Cheap and synchronous:
  // three storage reads and two navigator properties.
  const willBounce = shouldAutoLogin({
    ready: !isInitializing,
    hasAccount: account != null,
    online: navigator.onLine !== false,
    automated: Boolean(navigator.webdriver),
    returningVisitor: isReturningVisitor(),
    deliberateSignOut: hasSignedOutDeliberately(),
    alreadyAttempted: hasAttemptedAutoLogin(),
  })

  useEffect(() => {
    if (autoTriedRef.current) return
    // The guard set is canonical now (authAutoLogin.ts). Quil had two of the five, which is why
    // it bounced ANY signed-out visitor: a first-time one who had never seen the app, an
    // automated browser, and someone who had deliberately signed out seconds earlier in another
    // tab. It now has all five. W9.
    if (!willBounce) {
      // Do NOT latch autoTriedRef here: the guards above can legitimately flip (init settles,
      // the network returns), and latching on the first failing render would mean a returning
      // device never bounces at all.
      return
    }
    autoTriedRef.current = true
    markAutoLoginAttempted()
    // Promise.resolve guards a login() that doesn't return a promise; the async .catch
    // (not a synchronous setState) flips to the manual page if the redirect can't start.
    Promise.resolve(login()).catch(() => setAutoFailed(true))
  }, [willBounce, login])

  // Which screen belongs on screen is the canonical decision now (authGate.ts, W9). Quil's own
  // three-way offline handling is what that module was modelled on, so this is a move rather than
  // a change — and it fixes one thing the old expression would have got wrong once the auto-login
  // guards tightened: a brand-new visitor no longer bounces at all, so "have we already tried this
  // tab-session" stopped being a usable stand-in for "is a redirect actually in flight", and they
  // would have sat on "Signing you in…" forever.
  const view = decideAuthView({
    ready: !isInitializing,
    hasAccount: account != null,
    // A bounce is about to fire on this very render, so hold a neutral splash rather than
    // flashing the sign-in page at someone who is being signed in. Once it HAS fired,
    // `alreadyAttempted` makes this false, so a redirect that came back still-signed-out lands on
    // the manual page instead of a splash that never resolves. Derived, not state: this effect
    // must not setState synchronously (react-hooks/set-state-in-effect).
    autoLoginInFlight: willBounce,
    online: navigator.onLine !== false,
    trustedDevice,
    enteredOffline,
    returningVisitor: isReturningVisitor(),
    hasLandingPage: true, // P4: Quil has a landing page now, and it is the only signed-out screen
    timedOut: false, // the cold-start backstop below is a post-auth concern, not a gate one
  })

  // Offline, signed out, on a device that holds a library it knows is an account's: open exactly that
  // store (never adopting, stamping or creating one), then show the app. Once only.
  const wantsOffline = view.kind === 'app' && account == null
  useEffect(() => {
    if (!wantsOffline || offlineOpened || offlineFailed) return
    let live = true
    openOffline().then(
      opened => { if (live) { if (opened) setOfflineOpened(true); else setOfflineFailed(true) } },
      () => { if (live) setOfflineFailed(true) },
    )
    return () => { live = false }
  }, [wantsOffline, offlineOpened, offlineFailed, openOffline])

  if (view.kind === 'initialising') return <Splash />
  if (view.kind === 'signing-in' && !autoFailed) return <Splash label="Signing you in…" />
  // The offline library could not be opened (nothing of an account's is stored here after all): the
  // front door is the honest place to be, without an offline button that would lead nowhere.
  if (wantsOffline && offlineFailed) return <LandingPage onSignIn={login} canWorkOffline={false} onWorkOffline={() => {}} />
  // `landing` and `sign-in` render the same thing: the canonical decideAuthView still returns two
  // views on purpose, and collapsing them (moving `canWorkOffline` onto `landing`) belongs to
  // app-platform once all five apps have actually converged, not in anticipation of it.
  if (view.kind === 'landing' || view.kind === 'sign-in' || (view.kind === 'signing-in' && autoFailed)) {
    return (
      <LandingPage
        onSignIn={login}
        canWorkOffline={trustedDevice}
        onWorkOffline={() => setEnteredOffline(true)}
      />
    )
  }
  // view.kind === 'app'. A trusted device reaches here with no account at all, which is the
  // genuinely-offline (2.2) and chose-offline (D8) paths.
  if (!account) return offlineOpened || storeReady ? <>{children}</> : <Splash />
  if (storeState.status === 'failed') return <StoreFailedGate onRetry={retryStore} />
  if (!storeReady) return <Splash />
  if (authedPhase === 'syncing') return <LoadingGate progress={progress} />
  if (authedPhase === 'failed') return <FailedGate onRetry={retry} onContinue={() => setAuthedPhase('ready')} />
  if (authedPhase === 'ready') return <>{children}</>
  return <Splash />
}
